import { supabase } from './supabase'
import { logActivity } from './activityService'

// Get all users
export const getAllUsers = async (filters = {}) => {
  try {
    let query = supabase
      .from('users')
      .select('*')
      .neq('role', 'admin')
      .order('created_at', { ascending: false })

    if (filters.status) {
      query = query.eq('account_status', filters.status)
    }
    if (filters.search) {
      query = query.or(`username.ilike.%${filters.search}%,email.ilike.%${filters.search}%,full_name.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Get pending transactions
export const getPendingTransactions = async () => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select(`
        *,
        users (id, username, email, full_name, balance)
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: true })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Approve transaction
export const approveTransaction = async (transactionId, adminId) => {
  try {
    // Get transaction details
    const { data: transaction } = await supabase
      .from('transactions')
      .select('*, users(balance)')
      .eq('id', transactionId)
      .single()

    if (!transaction || transaction.status !== 'pending') {
      throw new Error('Transaction not found or already processed')
    }

    const currentBalance = transaction.users.balance
    let newBalance

    if (transaction.type === 'deposit') {
      newBalance = currentBalance + transaction.amount
    } else if (transaction.type === 'withdrawal') {
      if (currentBalance < transaction.amount) {
        throw new Error('Insufficient funds')
      }
      newBalance = currentBalance - transaction.amount
    }

    // Update user balance
    await supabase
      .from('users')
      .update({ balance: newBalance })
      .eq('id', transaction.user_id)

    // Update transaction
    const { data, error } = await supabase
      .from('transactions')
      .update({
        status: 'approved',
        balance_after: newBalance,
        approved_by: adminId,
        approved_at: new Date().toISOString()
      })
      .eq('id', transactionId)
      .select()
      .single()

    if (error) throw error

    await logActivity(adminId, 'admin_approve', `Approved ${transaction.type} of $${transaction.amount} for user ${transaction.user_id}`)

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Reject transaction
export const rejectTransaction = async (transactionId, adminId, reason = '') => {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .update({
        status: 'rejected',
        description: reason || 'Rejected by admin',
        approved_by: adminId,
        approved_at: new Date().toISOString()
      })
      .eq('id', transactionId)
      .eq('status', 'pending')
      .select()
      .single()

    if (error) throw error

    await logActivity(adminId, 'admin_reject', `Rejected transaction ${transactionId}`)

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Update user balance directly
export const updateUserBalance = async (userId, newBalance, adminId) => {
  try {
    // Get current balance
    const { data: user } = await supabase
      .from('users')
      .select('balance')
      .eq('id', userId)
      .single()

    const difference = newBalance - user.balance

    // Update balance
    await supabase
      .from('users')
      .update({ balance: newBalance })
      .eq('id', userId)

    // Create transaction record
    await supabase
      .from('transactions')
      .insert({
        user_id: userId,
        type: 'admin_adjustment',
        amount: difference,
        balance_before: user.balance,
        balance_after: newBalance,
        description: `Balance adjusted by admin`,
        status: 'completed',
        approved_by: adminId,
        approved_at: new Date().toISOString()
      })

    await logActivity(adminId, 'admin_update_balance', `Updated user ${userId} balance to $${newBalance}`)

    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

// Update user details
export const updateUser = async (userId, updates, adminId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()

    if (error) throw error

    await logActivity(adminId, 'admin_update_user', `Updated user ${userId} details`)

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Delete user
export const deleteUser = async (userId, adminId) => {
  try {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', userId)

    if (error) throw error

    await logActivity(adminId, 'admin_delete_user', `Deleted user ${userId}`)

    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

// Get all transactions
export const getAllTransactions = async (filters = {}, limit = 100) => {
  try {
    let query = supabase
      .from('transactions')
      .select(`
        *,
        users (username, email, full_name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (filters.type) {
      query = query.eq('type', filters.type)
    }
    if (filters.status) {
      query = query.eq('status', filters.status)
    }
    if (filters.userId) {
      query = query.eq('user_id', filters.userId)
    }

    const { data, error } = await query

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    return { data: null, error: error.message }
  }
}

// Delete transaction
export const deleteTransaction = async (transactionId, adminId) => {
  try {
    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', transactionId)

    if (error) throw error

    await logActivity(adminId, 'admin_delete_transaction', `Deleted transaction ${transactionId}`)

    return { error: null }
  } catch (error) {
    return { error: error.message }
  }
}

// Get dashboard statistics
export const getDashboardStats = async () => {
  try {
    // Get user stats - use status instead of account_status
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('balance, status, role')

    if (usersError) throw usersError

    // Filter out admins
    const nonAdminUsers = users?.filter(u => u.role !== 'admin') || []

    // Get transaction stats
    const { data: transactions, error: transError } = await supabase
      .from('transactions')
      .select('type, amount, status')

    if (transError) throw transError

    // Get pending count
    const { count: pendingCount, error: pendingError } = await supabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending')

    if (pendingError) throw pendingError

    const stats = {
      totalUsers: nonAdminUsers.length,
      activeUsers: nonAdminUsers.filter(u => u.status === 'active').length,
      totalBalance: nonAdminUsers.reduce((sum, u) => sum + (parseFloat(u.balance) || 0), 0),
      totalTransactions: transactions?.length || 0,
      pendingTransactions: pendingCount || 0,
      approvedTransactions: transactions?.filter(t => t.status === 'approved').length || 0,
      rejectedTransactions: transactions?.filter(t => t.status === 'rejected').length || 0
    }

    return { data: stats, error: null }
  } catch (error) {
    console.error('Dashboard stats error:', error)
    return { data: null, error: error.message }
  }
}

// Bulk approve transactions
export const bulkApproveTransactions = async (transactionIds, adminId) => {
  try {
    const results = []
    
    for (const id of transactionIds) {
      const result = await approveTransaction(id, adminId)
      results.push(result)
    }

    const successCount = results.filter(r => !r.error).length
    const failCount = results.filter(r => r.error).length

    return {
      success: successCount,
      failed: failCount,
      error: null
    }
  } catch (error) {
    return { success: 0, failed: transactionIds.length, error: error.message }
  }
}

export default {
  getAllUsers,
  getPendingTransactions,
  approveTransaction,
  rejectTransaction,
  updateUserBalance,
  updateUser,
  deleteUser,
  getAllTransactions,
  deleteTransaction,
  getDashboardStats,
  bulkApproveTransactions
}